// src/api/packageApi.js

import apiClient from "./client";

export const packageApi = {
  // Public method - no authentication required
  getPublicPackages: async (activeOnly = true) => {
    try {
      const response = await apiClient.get(
        `/packages/public?activeOnly=${activeOnly}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching public packages:", error);
      return { success: false, data: [] };
    }
  },

  // Authenticated methods
  getPackages: async (activeOnly = true) => {
    const response = await apiClient.get(`/packages?activeOnly=${activeOnly}`);
    return response.data;
  },

  getPackageById: async (id) => {
    const response = await apiClient.get(`/packages/${id}`);
    return response.data;
  },

  createPackage: async (data) => {
    const response = await apiClient.post("/packages", data);
    return response.data;
  },

  updatePackage: async (id, data) => {
    const response = await apiClient.put(`/packages/${id}`, data);
    return response.data;
  },

  deletePackage: async (id) => {
    const response = await apiClient.delete(`/packages/${id}`);
    return response.data;
  },
};

// ============================================================
// ONBOARDING API
// ============================================================
export const onboardingApi = {
  // ============================================================
  // ADMIN ENDPOINTS - Use /admin/onboarding
  // ============================================================

  // Get full configuration (Admin)
  getConfig: async () => {
    const response = await apiClient.get("/admin/onboarding/config");
    return response.data;
  },

  // Update full configuration (Admin)
  updateConfig: async (config) => {
    const response = await apiClient.put("/admin/onboarding/config", config);
    return response.data;
  },

  getAdminDomainSettings: async () => {
    const response = await apiClient.get("/admin/onboarding/domain-settings");
    return response.data;
  },

  saveAdminDomainSettings: async (settings) => {
    const response = await apiClient.put(
      "/admin/onboarding/domain-settings",
      settings,
    );
    return response.data;
  },

  // Reset to defaults (Admin)
  resetConfig: async () => {
    const response = await apiClient.post("/admin/onboarding/config/reset");
    return response.data;
  },

  // Get single step (Admin)
  getStep: async (stepKey) => {
    const response = await apiClient.get(`/admin/onboarding/steps/${stepKey}`);
    return response.data;
  },

  // Update single step (Admin)
  updateStep: async (stepKey, updates) => {
    const response = await apiClient.patch(
      `/admin/onboarding/steps/${stepKey}`,
      updates,
    );
    return response.data;
  },

  // ============================================================
  // CLIENT ENDPOINTS - Use /onboarding (for client store request)
  // ============================================================

  // Get all steps (Client - used by RequestStore)
  getAllSteps: async () => {
    const response = await apiClient.get("/onboarding/steps");
    return response.data;
  },

  // Get progress for a store request (Client)
  getProgress: async (storeRequestId) => {
    const response = await apiClient.get(
      `/onboarding/progress/${storeRequestId}`,
    );
    return response.data;
  },

  // Get steps for a specific store request (Client)
  getStoreSteps: async (storeRequestId) => {
    const response = await apiClient.get(`/onboarding/steps/${storeRequestId}`);
    return response.data;
  },

  // Get data for a specific step (Client)
  getStepData: async (storeRequestId, stepId) => {
    const response = await apiClient.get(
      `/onboarding/step/${storeRequestId}/${stepId}`,
    );
    return response.data;
  },

  // Update a step (Client)
  updateClientStep: async (storeRequestId, stepId, status, data) => {
    const response = await apiClient.put(
      `/onboarding/step/${storeRequestId}/${stepId}`,
      { status, data },
    );
    return response.data;
  },

  // Submit for review (Client)
  submitForReview: async (storeRequestId) => {
    const response = await apiClient.post(
      `/onboarding/submit/${storeRequestId}`,
    );
    return response.data;
  },

  // Get domain extensions (Client)
  getDomains: async () => {
    const response = await apiClient.get("/onboarding/domains");
    return response.data;
  },

  // Get design templates (Client)
  getTemplates: async () => {
    const response = await apiClient.get("/onboarding/templates");
    return response.data;
  },

  // Get agreement sections (Client)
  getAgreement: async () => {
    const response = await apiClient.get("/onboarding/agreement");
    return response.data;
  },

  getContractSettings: async () => {
    const response = await apiClient.get("/onboarding/contract-settings");
    return response.data;
  },

  // ============================================================
  // DOMAIN SETTINGS - CLIENT ENDPOINT
  // ============================================================

  // Get domain settings (Client)
  getDomainSettings: async () => {
    const response = await apiClient.get("/onboarding/domain-settings");
    return response.data;
  },
};

export const contractApi = {
  getAdminLibrary: async () => {
    const response = await apiClient.get("/admin/contracts");
    return response.data;
  },

  uploadLogo: async (name, dataUri) => {
    const response = await apiClient.post("/admin/contracts/logos", {
      name,
      dataUri,
    });
    return response.data;
  },

  deleteLogo: async (logoId) => {
    const response = await apiClient.delete(`/admin/contracts/logos/${logoId}`);
    return response.data;
  },

  saveTemplates: async (contracts) => {
    const response = await apiClient.put("/admin/contracts/templates", {
      contracts,
    });
    return response.data;
  },
};
